import { useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://winton-partners-api.bcirbis.workers.dev';

interface Partner {
  id: string;
  userId: string;
  username?: string;
  balance: number;
  totalEarned: number;
  totalWithdrawn: number;
  referralCount: number;
  activeReferrals: number;
  level: string;
  joinedAt: string;
  lastActive: string;
}

interface RefLinkInfo {
  ref_id: string;
  ref_name: string;
  ref_code: string;
  commission_rate: number;
  total_deposits: number;
  balance: number;
  players_count: number;
}

interface Statistics {
  overview: {
    totalRevenue: number;
    totalClicks: number;
    totalRegistrations: number;
    totalDeposits: number;
    conversionRate: number;
  };
  daily: Array<{
    date: string;
    revenue: number;
    clicks: number;
    registrations: number;
    deposits: number;
    activeUsers: number;
  }>;
  topReferrals: Array<{
    userId: string;
    username: string;
    revenue: number;
    registeredAt: string;
  }>;
  refLinks?: RefLinkInfo[];
}

interface Referral {
  id: string;
  referrerId: string;
  userId: string;
  username?: string;
  status: 'active' | 'inactive';
  totalRevenue: number;
  registeredAt: string;
  lastActiveAt: string;
}

export function usePartnerData(initDataRaw: string, refLinkFilter?: string) {
  const [profile, setProfile] = useState<Partner | null>(null);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!initDataRaw) {
      console.log('usePartnerData: No initDataRaw provided');
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('Fetching data from:', API_URL);
        console.log('InitData length:', initDataRaw.length);

        const headers = {
          'Content-Type': 'application/json',
          'X-Telegram-Init-Data': initDataRaw,
        };

        // Загружаем все данные параллельно
        const statsUrl = refLinkFilter 
          ? `${API_URL}/api/partner/statistics?period=week&refLink=${encodeURIComponent(refLinkFilter)}`
          : `${API_URL}/api/partner/statistics?period=week`;
          
        const [profileRes, statsRes, referralsRes] = await Promise.all([
          fetch(`${API_URL}/api/partner/profile`, { headers }),
          fetch(statsUrl, { headers }),
          fetch(`${API_URL}/api/partner/referrals`, { headers }),
        ]);

        console.log('Profile response status:', profileRes.status);
        console.log('Stats response status:', statsRes.status);
        console.log('Referrals response status:', referralsRes.status);

        if (!profileRes.ok) {
          const errorData = await profileRes.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Profile API error:', errorData);
          throw new Error(errorData.message || errorData.error || 'Failed to fetch profile');
        }

        if (!statsRes.ok || !referralsRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const [profileData, statsData, referralsData] = await Promise.all([
          profileRes.json(),
          statsRes.json(),
          referralsRes.json(),
        ]);

        console.log('Data fetched successfully');
        setProfile(profileData.data);
        setStatistics(statsData.data);
        setReferrals(referralsData.data);
      } catch (err: any) {
        console.error('Error fetching partner data:', err);
        setError(err.message || 'Не удалось загрузить данные');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [initDataRaw, refLinkFilter]);

  return {
    profile,
    statistics,
    referrals,
    isLoading,
    error,
  };
}
